/**
 * Cloud-mode picker that lets the user link the project to one of the
 * org's saved deploy targets. Falls back to "+ Add new target" inline,
 * which uses the existing DeployTargetField for kind-specific inputs
 * and POSTs to /v1/orgs/:slug/deploy-targets, then auto-selects the
 * fresh row.
 *
 * Free mode never renders this — secrets travel with the project there
 * and are edited by the regular DeployTargetField in the dialog.
 */

import type { DeployTargetConfig } from '@bliksys/shared';
import { useEffect, useState } from 'react';

import { type DeployTargetSummary, orgApi, type OrgApi } from '../data/orgApi.js';

import { DeployTargetField, labelDeployKind, validateDeployTarget } from './DeployTargetField.js';
import './DeployTargetPicker.css';

interface Props {
  orgSlug: string;
  /** Current value — the org-scoped target id linked to the project,
   * or null/undefined when the project has none. */
  value: string | null | undefined;
  onChange: (id: string | null) => void;
}

type AddState =
  | { kind: 'closed' }
  | {
      kind: 'open';
      draft: DeployTargetConfig | undefined;
      name: string;
      busy: boolean;
      error: string | null;
    };

export function DeployTargetPicker({ orgSlug, value, onChange }: Props): JSX.Element {
  const api: OrgApi = orgApi({ orgSlug });
  const [targets, setTargets] = useState<DeployTargetSummary[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [addState, setAddState] = useState<AddState>({ kind: 'closed' });

  const refresh = async (): Promise<void> => {
    try {
      const list = await api.listDeployTargets();
      setTargets(list);
      setLoadError(null);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : 'Failed to load deploy targets.');
    }
  };

  useEffect(() => {
    void refresh();
    // refresh has a stable identity per render but doesn't depend on
    // anything that changes during this dialog's lifetime.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgSlug]);

  const handleAddOpen = (): void => {
    setAddState({ kind: 'open', draft: undefined, name: '', busy: false, error: null });
  };

  const handleAddSubmit = async (): Promise<void> => {
    if (addState.kind !== 'open') return;
    const trimmedName = addState.name.trim();
    if (!trimmedName) {
      setAddState({ ...addState, error: 'Name is required.' });
      return;
    }
    if (!addState.draft) {
      setAddState({ ...addState, error: 'Pick a kind and fill in the fields.' });
      return;
    }
    const errors = validateDeployTarget(addState.draft);
    const firstError = Object.values(errors)[0];
    if (firstError) {
      setAddState({ ...addState, error: firstError });
      return;
    }
    setAddState({ ...addState, busy: true, error: null });
    try {
      const created = await api.createDeployTarget({ name: trimmedName, config: addState.draft });
      setAddState({ kind: 'closed' });
      await refresh();
      onChange(created.id);
    } catch (err) {
      setAddState({
        ...addState,
        busy: false,
        error: err instanceof Error ? err.message : 'Failed to create.',
      });
    }
  };

  return (
    <div className="deploy-target-picker">
      {loadError && (
        <p className="deploy-target-picker-error" role="alert">
          {loadError}
        </p>
      )}

      <label className="deploy-target-picker-label">
        <span>Linked deploy target</span>
        <select
          className="input"
          value={value ?? ''}
          onChange={(e) => onChange(e.target.value === '' ? null : e.target.value)}
          disabled={targets === null}
        >
          <option value="">None</option>
          {(targets ?? []).map((t) => (
            <option key={t.id} value={t.id}>
              {t.name} · {labelDeployKind(t.kind)}
            </option>
          ))}
        </select>
      </label>

      {addState.kind === 'closed' ? (
        <button type="button" className="deploy-target-picker-add-toggle" onClick={handleAddOpen}>
          + Add new deploy target
        </button>
      ) : (
        <div className="deploy-target-picker-add">
          <label className="deploy-target-picker-label">
            <span>Name</span>
            <input
              type="text"
              className="input"
              value={addState.name}
              onChange={(e) => setAddState({ ...addState, name: e.target.value })}
              placeholder="e.g. Production GitHub"
            />
          </label>
          <DeployTargetField
            target={addState.draft}
            onChange={(draft) => setAddState({ ...addState, draft })}
          />
          {addState.error && (
            <p className="deploy-target-picker-error" role="alert">
              {addState.error}
            </p>
          )}
          <div className="deploy-target-picker-add-actions">
            <button
              type="button"
              className="landing-button-text"
              onClick={() => setAddState({ kind: 'closed' })}
              disabled={addState.busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="landing-button-primary"
              onClick={() => void handleAddSubmit()}
              disabled={addState.busy}
            >
              {addState.busy ? 'Saving…' : 'Save target'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
